import { Result, SearchDocument } from '@backstage/plugin-search-common';

/**
 * Groups the documentation hits of one page of search results so that a page of
 * documentation appears once instead of once per heading it matched.
 *
 * MkDocs indexes every heading as its own entry alongside the whole page, and
 * the TechDocs collator turns each of those into a search document, so for
 * `core-platform-docs` 26 pages become 475 documents. Because the page-level
 * entry's text is the entire page, a term in one section matches both that
 * section and the page, and a term used throughout a document matches most of
 * its sections: measured against production, one query returned 25 results
 * drawn from only 7 documents, 9 of them from a single page.
 *
 * Collapsing this at query time needs OpenSearch's field collapsing, which is
 * not available while search runs on the in-memory Lunr engine, so it is done
 * here instead. The grouping key is deliberately the same one a `collapse`
 * would use, so the logic can move to the backend unchanged if that lands.
 */

/** Result shape handed to us by `SearchResult`'s children. */
export type SearchResultItem = Result<SearchDocument> & { rank?: number };

export type ResultGroup = {
  /** Normalized page path shared by every member. */
  key: string;
  /** Best-ranked hit, whose snippet represents the group. */
  primary: SearchResultItem;
  /**
   * The remaining hits, all of them sections of the same page. Empty for a
   * page that matched once, which is rendered as a plain result.
   */
  sections: SearchResultItem[];
  /**
   * Title of the page itself, when the page-level entry is one of the hits.
   * Section titles are heading titles, so without this a group can be headed
   * "Executive summary" with no clue which document it belongs to.
   */
  pageTitle?: string;
};

const DOCS_PATH =
  /^(\/docs\/[^/]+\/)(component|system|api|resource|domain|group|user|location|template)(\/.*)$/i;

/**
 * The key that decides "same document". Two things have to be normalized away.
 *
 * The `#heading` anchor, because every section of a page is a separate hit.
 *
 * The entity kind, because a repository that puts `techdocs-ref` on both its
 * System and its Component publishes the same pages twice, and both copies are
 * indexed and rank next to each other. `core-iam`, `core-accounts` and
 * `core-service-management` all do this today; the annotation is being removed
 * in those repositories, but the portal should not depend on that being done
 * everywhere, forever.
 */
export function docGroupKey(location: string): string {
  const path = location.split('#')[0];
  const match = DOCS_PATH.exec(path);
  return match ? `${match[1]}*${match[3]}` : path;
}

function isSection(location: string): boolean {
  return location.includes('#');
}

/**
 * Walks one page of results in rank order, replacing each run of hits that
 * belong to the same documentation page with a single group. Everything else
 * (catalog entities, releases) passes through untouched and keeps its position,
 * so relative ranking across result types is preserved.
 */
export function groupDocResults(
  results: SearchResultItem[],
  options: { types?: string[] } = {},
): (SearchResultItem | ResultGroup)[] {
  const groupedTypes = options.types ?? ['techdocs'];
  const output: (SearchResultItem | ResultGroup)[] = [];
  const groups = new Map<string, ResultGroup>();
  // Anchors already represented in a group, the primary's included. Without the
  // primary in here, the System and Component copies of the *first* hit of a
  // page end up as a group headed by one and listing the other as a section.
  const seenAnchors = new Map<string, Set<string>>();

  for (const result of results) {
    const location = result.document.location;
    if (!groupedTypes.includes(result.type) || !location) {
      output.push(result);
      continue;
    }

    const key = docGroupKey(location);
    const existing = groups.get(key);
    if (existing) {
      // A page-level hit arriving after a section hit is not shown as its own
      // entry, but it does tell us the page's real title.
      if (!isSection(location)) {
        existing.pageTitle = result.document.title;
        existing.sections = existing.sections.filter(
          section => !isPageHeading(section, existing.pageTitle),
        );
      } else if (isPageHeading(result, existing.pageTitle)) {
        // The h1 of a page carries the page's own title, so offering it as a
        // section is offering a link to the top of the page we are already
        // linking to. This is the duplication upstream reports in
        // backstage/mkdocs-techdocs-core#192.
        continue;
      } else if (!seenAnchors.get(key)!.has(docAnchor(result))) {
        seenAnchors.get(key)!.add(docAnchor(result));
        existing.sections.push(result);
      }
      continue;
    }

    const group: ResultGroup = {
      key,
      primary: result,
      sections: [],
      ...(isSection(location) ? {} : { pageTitle: result.document.title }),
    };
    groups.set(key, group);
    seenAnchors.set(key, new Set([docAnchor(result)]));
    output.push(group);
  }

  return output;
}

/** Anchor of a hit, used to drop the System/Component copies of one section. */
function docAnchor(result: SearchResultItem): string {
  return result.document.location.split('#')[1] ?? '';
}

/** Whether a section is really the page's own h1, by carrying its title. */
function isPageHeading(
  result: SearchResultItem,
  pageTitle: string | undefined,
): boolean {
  return (
    pageTitle !== undefined && result.document.title.trim() === pageTitle.trim()
  );
}

export function isResultGroup(
  item: SearchResultItem | ResultGroup,
): item is ResultGroup {
  return (item as ResultGroup).key !== undefined;
}

/**
 * A name for the page a hit belongs to, taken from its path.
 *
 * Needed because a group whose hits are all sections has no page title to show:
 * MkDocs gives a section only its heading text, so the result would read "8.1
 * Telemetry Dashboarding and MQL Alerting" with nothing to say it is part of the
 * Google Cloud service infrastructure document. The file name is not a title,
 * but it identifies the document, which is what the reader is missing.
 *
 * Returns undefined when the page is the entity's own documentation root, where
 * the last segment is the entity name and repeating it would add nothing.
 */
export function pageLabelFromLocation(location: string): string | undefined {
  const path = location.split('#')[0].replace(/\/+$/, '');
  const match = DOCS_PATH.exec(`${path}/`);
  if (!match) {
    return undefined;
  }
  // match[3] is everything after the entity kind, i.e. `/<name>/<page path>`.
  const segments = match[3].split('/').filter(Boolean);
  // [name] alone is the documentation root of that entity.
  return segments.length > 1 ? segments[segments.length - 1] : undefined;
}
